import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTimezone } from '@/components/TimezoneContext';
import { useA, todayIn } from '@/analytics/lib';
import { DialogProvider, useDialogs } from '@/trace/dialogs';
import { Icon, Logo } from '@/trace/icons';
import { Badge, initials } from '@/trace/ui';
import { useAutomations, useMetaConnection, useRecentActivity, useRegistrations, useSales, useSavedReports, useSendbackConfig, useStats } from '@/trace/data';
import { CommandPalette, HealthBody, HealthFooter, HelpBody, HelpFooter, NotificationsBody, NotificationsFooter, ProfileBody, ProfileFooter, WorkspaceBody, WorkspaceFooter, healthFacts } from '@/trace/shellDialogs';
import { ShellContext } from './shellContext';
import './workspace.css';
import './interaction.css';
import '@/trace/trace.css';
import '@/trace/tether.css';

// The Trace prototype's frame, one to one: the sidebar (brand, workspace card, WORKSPACE / MANAGE groups, the health
// card, Settings, Help & shortcuts, the profile) that folds into a 68px rail; the top bar (rail toggle, breadcrumb,
// workspace badge, ⌘K search, notifications); the page with its footer. Phones get the sliding drawer.
export const PAGES = [
  { id: 'overview', path: '/', label: 'Overview', icon: 'grid', group: 'workspace', report: true, match: p => p === '/' },
  { id: 'campaigns', path: '/campaigns', label: 'Campaigns', icon: 'chart', group: 'workspace', report: true, match: p => p.startsWith('/campaigns') },
  { id: 'attribution', path: '/attribution', label: 'Attribution', icon: 'target', group: 'workspace', report: true, match: p => p.startsWith('/attribution') },
  { id: 'journeys', path: '/journeys', label: 'Customer journeys', icon: 'journey', group: 'workspace', match: p => ['/journeys', '/people', '/leads', '/visitors', '/sales', '/stealth'].some(x => p.startsWith(x)) },
  { id: 'traffic', path: '/traffic', label: 'Traffic', icon: 'activity', group: 'workspace', report: true, match: p => p.startsWith('/traffic') || p.startsWith('/analytics') },
  { id: 'reports', path: '/reports', label: 'Saved reports', icon: 'report', group: 'manage', match: p => p.startsWith('/reports') },
  { id: 'connections', path: '/connections', label: 'Connections', icon: 'plug', group: 'manage', match: p => p.startsWith('/connections') || p.startsWith('/sendback') },
  { id: 'automations', path: '/automations', label: 'Automations', icon: 'zap', group: 'manage', match: p => p.startsWith('/automations') },
  { id: 'settings', path: '/settings', label: 'Settings', icon: 'settings', group: 'bottom', match: p => p.startsWith('/settings') },
];
const SUBPAGES = [['/logs', 'Activity'], ['/sendback', 'Send-back'], ['/people', 'People'], ['/leads', 'People'], ['/visitors', 'Visitors'], ['/sales', 'Conversions'], ['/stealth', 'Registrations'], ['/automations/new', 'New automation'], ['/automations/builder', 'Edit automation']];
const KEEP = ['range', 'since', 'until', 'model', 'window', 'revenue', 'compare', 'source', 'medium', 'campaign', 'content', 'term', 'campaign_id', 'adset_id', 'ad_id', 'page', 'host'];
const RAIL_KEY = 'tether_sidebar';   // 'collapsed' | 'expanded' (the default)
const MOBILE = '(max-width: 760px)';
const COMPACT = '(min-width: 480px) and (max-width: 760px)';
const DESKTOP = '(min-width: 761px)';

const mq = query => (typeof window.matchMedia === 'function' ? window.matchMedia(query) : null);
const matches = query => !!mq(query)?.matches;
function readRailState() { try { return localStorage.getItem(RAIL_KEY) === 'collapsed'; } catch { return false; } }

// The wordmark outside the frame (sign-in, onboarding): the workspace stylesheet's `.sp-wordmark`.
export function Wordmark() {
  return (
    <span className="sp-wordmark">
      <span className="sp-brand-mark" aria-hidden="true"><Logo /></span>
      <span className="sp-wordmark-text">tether</span>
    </span>
  );
}

// The 24 hourly bars behind "Tracking is healthy": today's page views, hour by hour.
function useTodayBars(enabled) {
  const { timezone } = useTimezone();
  const today = todayIn(timezone);
  const state = useMemo(() => ({ since: today, until: today, tz: timezone, model: 'last', window: '30', revenue: 'net', filters: {} }), [today, timezone]);
  const q = useA('/timeseries', state, { granularity: 'hour' }, { enabled, refetchInterval: 300_000 });
  return (q.data?.series || []).map(b => b.visits || 0);
}

function Shell({ me, adminMode, onLogout, onSwitchWorkspace, children }) {
  const { pathname, search } = useLocation();
  const { timezone } = useTimezone();
  const { openDialog, isOpen } = useDialogs();
  const [collapsed, setCollapsed] = useState(readRailState);
  const [mobile, setMobile] = useState(false);          // the drawer is open
  const [isMobile, setIsMobile] = useState(() => matches(MOBILE));
  const [compact, setCompact] = useState(() => matches(COMPACT));
  const sidebarRef = useRef(null);
  const menuButtonRef = useRef(null);
  const toggleRef = useRef(null);
  const authed = !!me;

  const current = PAGES.find(p => p.match(pathname));
  const pageName = SUBPAGES.find(([prefix]) => pathname.startsWith(prefix))?.[1] || current?.label || 'Page not found';
  const effectiveCollapsed = compact ? !mobile : collapsed;
  const overlayOpen = isMobile && mobile;

  // Live counts for the MANAGE group and the health card.
  const reports = useSavedReports(authed);
  const automations = useAutomations(authed);
  const stats = useStats(authed);
  const sales = useSales(authed);
  const meta = useMetaConnection(timezone, authed);
  const sendback = useSendbackConfig(authed);
  const regs = useRegistrations(authed && me?.funnel_type === 'webinar');
  const activity = useRecentActivity(authed);
  const bars = useTodayBars(authed);
  const salesList = Array.isArray(sales.data) ? sales.data : [];
  const regList = Array.isArray(regs.data) ? regs.data : [];
  const connectedCount = [meta.data?.connected, sendback.data?.configured && sendback.data?.enabled, salesList.length > 0, regList.length > 0].filter(Boolean).length;
  const healthy = (stats.data?.today_visits || 0) > 0;
  const activeAutomations = (Array.isArray(automations.data) ? automations.data : []).filter(a => a.enabled).length;

  const toggleRail = () => {
    if (compact) { setMobile(v => !v); return; }
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(RAIL_KEY, next ? 'collapsed' : 'expanded'); } catch { /* private mode: the choice lasts for this session */ }
  };
  const closeDrawer = useCallback(() => setMobile(false), []);

  useEffect(() => { setMobile(false); window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => {
    const onMobile = e => setIsMobile(e.matches);
    const onCompact = e => setCompact(e.matches);
    const closeAtDesktop = e => { if (e.matches) setMobile(false); };
    const m = mq(MOBILE), c = mq(COMPACT), d = mq(DESKTOP);
    m?.addEventListener('change', onMobile); c?.addEventListener('change', onCompact); d?.addEventListener('change', closeAtDesktop);
    return () => { m?.removeEventListener('change', onMobile); c?.removeEventListener('change', onCompact); d?.removeEventListener('change', closeAtDesktop); };
  }, []);
  useEffect(() => {
    if (!mobile) return undefined;
    const sidebar = sidebarRef.current;
    const toggle = toggleRef.current;
    const menuButton = menuButtonRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('navigation-open');
    const closeButton = sidebar?.querySelector('.mobile-close');
    const focusClose = () => { if (closeButton && document.activeElement !== closeButton) closeButton.focus(); };
    focusClose();
    const frame = requestAnimationFrame(focusClose);
    const onKey = e => {
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') { setMobile(false); return; }
      if (e.key !== 'Tab' || !sidebar) return;
      const controls = [...sidebar.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')].filter(el => el.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('navigation-open');
      const back = compact ? toggle : menuButton;
      if (back?.getClientRects().length) back.focus();
    };
  }, [mobile, compact]);
  // ⌘K opens the search from anywhere.
  useEffect(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setMobile(false); openDialog({ command: true, label: 'Search workspace', body: <CommandPalette pages={PAGES} /> }); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openDialog]);

  const showHelp = () => openDialog({ title: 'A little help, a lot of clarity', body: <HelpBody />, footer: <HelpFooter /> });
  const showSearch = () => openDialog({ command: true, label: 'Search workspace', body: <CommandPalette pages={PAGES} /> });
  const showNotifications = () => openDialog({ title: 'Notifications', body: <NotificationsBody activity={activity} />, footer: <NotificationsFooter /> });
  const showHealth = () => openDialog({ title: 'Tracking health', body: <HealthBody healthy={healthy} facts={healthFacts({ stats: stats.data, sales: salesList, regs: me?.funnel_type === 'webinar' ? regList : null, connectedCount })} />, footer: <HealthFooter /> });
  const showWorkspace = () => openDialog({ title: 'Your workspace', body: <WorkspaceBody me={me} adminMode={adminMode} />, footer: <WorkspaceFooter onSwitchWorkspace={onSwitchWorkspace} /> });
  const showProfile = () => openDialog({ title: 'Your profile', body: <ProfileBody me={me} adminMode={adminMode} />, footer: <ProfileFooter onLogout={onLogout} /> });
  const sidebarAction = fn => () => { if (overlayOpen) setMobile(false); fn(); };

  const keep = useMemo(() => {
    const sp = new URLSearchParams(search); const next = new URLSearchParams();
    KEEP.forEach(k => { if (sp.get(k)) next.set(k, sp.get(k)); });
    const s = next.toString();
    return s ? `?${s}` : '';
  }, [search]);
  const linkTo = page => (page.report && current?.report ? `${page.path}${keep}` : page.path);
  const navItem = page => (
    <Link key={page.id} to={linkTo(page)} className={`nav-item${page === current ? ' active' : ''}`} aria-current={page === current ? 'page' : undefined}
      aria-label={page.label} data-tooltip={page.label} data-testid={`sidebar-${page.id}`} onClick={() => setMobile(false)}>
      <Icon name={page.icon} /><span className="nav-label">{page.label}</span>
      {page.id === 'reports' && Array.isArray(reports.data) && reports.data.length > 0 && <span className="nav-count">{reports.data.length}</span>}
      {page.id === 'connections' && connectedCount > 0 && <span className="nav-count">{connectedCount}</span>}
      {page.id === 'automations' && activeAutomations > 0 && <span className="nav-count">{activeAutomations}</span>}
    </Link>
  );
  const toggleLabel = `${effectiveCollapsed ? 'Expand' : 'Collapse'} sidebar`;
  const name = me?.name || 'Workspace';

  return (
    <ShellContext.Provider value={{ section: current?.group === 'manage' ? 'Manage' : current?.group ? 'Workspace' : null, page: pageName }}>
      <div className="sp-app trace">
        <a className="skip-link" href="#main">Skip to content</a>
        <aside ref={sidebarRef} id="dashboard-sidebar" className={`sidebar${overlayOpen ? ' is-open' : ''}${effectiveCollapsed ? ' is-collapsed' : ''}`}
          aria-label="Main navigation" role={overlayOpen ? 'dialog' : undefined} aria-modal={overlayOpen || undefined} inert={isMobile && !compact && !mobile ? true : undefined} data-testid="app-sidebar">
          <div className="brand">
            <Link to="/" aria-label="Tether home" data-tooltip="Tether" className="row" style={{ gap: 10, fontSize: 'inherit', fontWeight: 'inherit', letterSpacing: 'inherit' }}>
              <span className="brand-mark"><Logo /></span><span className="brand-name">tether</span>
            </Link>
            <button type="button" className="btn ghost icon-btn mobile-close" onClick={() => setMobile(false)} aria-label={compact ? 'Collapse sidebar' : 'Close navigation'} title={compact ? 'Collapse sidebar' : 'Close navigation'}>
              <Icon name="close" className="drawer-close-icon" /><Icon name="panel" className="rail-collapse-icon" />
            </button>
          </div>
          <button type="button" className="workspace" data-testid="sidebar-user-menu" onClick={sidebarAction(showWorkspace)} aria-label={`Workspace: ${name}`} data-tooltip={name}>
            <span className="workspace-logo">{name.trim().charAt(0).toLowerCase() || 't'}.</span>
            <span className="grow workspace-copy">{name}<small>{adminMode ? 'Admin session' : me?.email || 'Workspace'}</small></span>
            <Icon name="chevron" small />
          </button>
          <div className="nav-group"><span className="nav-title">WORKSPACE</span><nav aria-label="Workspace">{PAGES.filter(p => p.group === 'workspace').map(navItem)}</nav></div>
          <div className="nav-group"><span className="nav-title">MANAGE</span><nav aria-label="Manage">{PAGES.filter(p => p.group === 'manage').map(navItem)}</nav></div>
          <div className="sidebar-bottom">
            <button type="button" className="health" onClick={sidebarAction(showHealth)} aria-label={`Tracking health: ${healthy ? 'all systems operational' : 'no events today'}`} data-tooltip={healthy ? 'Tracking is healthy' : 'No events today'}>
              <span className="health-compact" aria-hidden="true"><Icon name="shield" /></span>
              <span className="health-details">
                <span className="row"><span className={`dot ${healthy ? 'positive' : 'muted'}`} />{healthy ? 'Tracking is healthy' : 'No events today'} <Icon name="arrowup" small /></span>
                <span className="health-bars" aria-hidden="true">{Array.from({ length: 24 }, (_, i) => {
                  const max = Math.max(1, ...bars); const v = bars[i] ?? 0;
                  return <i key={i} style={{ height: `${bars.length ? Math.max(12, (v / max) * 100) : 100}%`, opacity: bars.length && !v ? .35 : 1 }} />;
                })}</span>
                <span className="health-caption">{healthy ? 'All systems operational' : 'Waiting for tracked visits'}</span>
              </span>
            </button>
            <nav aria-label="Workspace settings">{PAGES.filter(p => p.group === 'bottom').map(navItem)}</nav>
            <button type="button" className="nav-item" onClick={sidebarAction(showHelp)} aria-label="Help & shortcuts" data-tooltip="Help & shortcuts"><Icon name="help" /><span className="nav-label">Help &amp; shortcuts</span></button>
            <button type="button" className="profile" onClick={sidebarAction(showProfile)} aria-label={`${name}, ${adminMode ? 'admin session' : 'workspace owner'}`} data-tooltip="Your profile" data-testid="sidebar-profile">
              <span className="avatar">{initials(name, 'T')}</span>
              <span className="grow profile-copy"><p>{name}</p><small>{adminMode ? 'Admin session' : 'Workspace owner'}</small></span>
              <Icon name="chevron" small />
            </button>
          </div>
        </aside>
        <button type="button" className={`sidebar-backdrop${overlayOpen ? ' is-visible' : ''}`} aria-label="Dismiss navigation" tabIndex={-1} inert={!overlayOpen ? true : undefined} onClick={closeDrawer} />
        <div className="shell" inert={overlayOpen || isOpen ? true : undefined}>
          <header className="topbar">
            <div className="breadcrumb">
              <button ref={toggleRef} type="button" className="btn ghost icon-btn sidebar-toggle" onClick={toggleRail} aria-controls="dashboard-sidebar" aria-expanded={!effectiveCollapsed} aria-label={toggleLabel} title={toggleLabel}>
                <Icon name="panel" /><span className="sidebar-toggle-label">{toggleLabel}</span>
              </button>
              <button ref={menuButtonRef} type="button" className="btn ghost icon-btn mobile-menu" onClick={() => setMobile(true)} aria-controls="dashboard-sidebar" aria-expanded={overlayOpen} aria-label="Open navigation"><Icon name="menu" /></button>
              <span className="muted">{current?.group === 'manage' ? 'Manage' : 'Workspace'}</span><span>/</span><strong>{pageName}</strong>
            </div>
            <div className="topbar-actions">
              <Badge>{adminMode ? 'Admin session' : name}</Badge><span className="topbar-separator" />
              <button type="button" className="global-search" onClick={showSearch} aria-label="Search everything"><Icon name="search" /><span>Search anything</span><span className="kbd">⌘ K</span></button>
              <button type="button" className="btn ghost icon-btn notification-button" onClick={showNotifications} aria-label="View notifications"><Icon name="bell" /></button>
            </div>
          </header>
          <main className="content" id="main" tabIndex={-1}>
            {children}
            <footer className="page-footer">
              <span className="row"><span className={`dot ${healthy ? 'positive' : 'muted'}`} />{healthy ? `Live data · ${stats.data.today_visits} events today` : 'Live data · no events today'}</span>
              <span><span className="type-currency">USD</span> · {timezone} · <sup className="type-sup">1</sup> Revenue net of refunds</span>
            </footer>
          </main>
        </div>
      </div>
    </ShellContext.Provider>
  );
}

export default function WorkspaceShell(props) {
  return <DialogProvider><Shell {...props} /></DialogProvider>;
}
